import { useEffect } from "react";
import { useLocation } from "wouter";
import { useAuth, logout } from "@/hooks/use-auth";
import { useInspections, useCreateInspection, defaultInspectionData } from "@/hooks/use-inspections";
import { Wrench, Plus, Check, Edit2, Loader2, LogOut, ChevronRight, ArrowLeft } from "lucide-react";
import logo from "/images/brand/vertex-logo.png";
import { Link } from "wouter";

export default function EngineerPPM() {
  const { data: user, isLoading: isAuthLoading } = useAuth();
  const [, setLocation] = useLocation();
  const { data: allInspections, isLoading: isInspectionsLoading, isError } = useInspections();
  const createInspection = useCreateInspection();

  useEffect(() => {
    if (!isAuthLoading) {
      if (!user) {
        setLocation("/admin"); // Redirects to login
      } else if (user.role === "admin") {
        setLocation("/admin/dashboard");
      }
    }
  }, [user, isAuthLoading, setLocation]);

  if (isAuthLoading || !user) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4 text-center">
        <Wrench className="w-12 h-12 text-red-500 mb-4" />
        <h2 className="text-xl font-bold text-slate-900 mb-2">Error Loading PPMs</h2>
        <p className="text-slate-600 mb-6">There was a problem loading your assigned PPM inspections.</p>
        <Link href="/engineer" className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors">Return to Dashboard</Link>
      </div>
    );
  }

  const myInspections = allInspections?.filter(i => i.engineerId === user.id) || [];

  const handleNewInspection = () => {
    createInspection.mutate({ status: "draft", data: defaultInspectionData }, {
      onSuccess: (newInsp) => {
        setLocation(`/engineer/inspection/${newInsp.id}`);
      }
    });
  };

  return (
    <div className="min-h-[100dvh] bg-slate-50 flex flex-col">
      <header className="bg-slate-900 border-b border-slate-800 sticky top-0 z-30 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={logo} alt="VertexAir" className="h-7 brightness-0 invert" />
            <div className="h-6 w-px bg-slate-700 mx-1"></div>
            <div className="flex items-center gap-2 text-white">
              <Wrench className="w-4 h-4 text-primary" />
              <span className="font-semibold text-sm">Engineer Portal</span>
            </div>
          </div>
          <button onClick={logout} className="p-2 -mr-2 text-slate-400 hover:text-white transition-colors" title="Logout">
            <LogOut className="w-5 h-5" />
          </button>
        </div>
      </header>

      <main className="flex-1 max-w-4xl w-full mx-auto p-4 space-y-6">
        <div className="flex items-center gap-3 mb-2">
          <Link href="/engineer" className="p-2 -ml-2 rounded-lg hover:bg-slate-200 text-slate-500 hover:text-slate-900 transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <h1 className="text-xl font-bold text-slate-900">PPM Inspections</h1>
        </div>

        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-slate-500">Manage your assigned PPM jobs</p>
          </div>
          <button
            onClick={handleNewInspection}
            disabled={createInspection.isPending}
            className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-white px-4 py-2.5 rounded-lg text-sm font-medium transition-colors shadow-sm disabled:opacity-50"
          >
            {createInspection.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            <span className="hidden sm:inline">New Draft</span>
            <span className="sm:hidden">New</span>
          </button>
        </div>

        {isInspectionsLoading ? (
          <div className="py-12 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-slate-300" /></div>
        ) : (
          <div className="space-y-3">
            {myInspections.length === 0 ? (
              <div className="bg-white border border-slate-200 rounded-xl p-8 text-center">
                <div className="w-12 h-12 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Wrench className="w-6 h-6 text-slate-400" />
                </div>
                <h3 className="font-semibold text-slate-900">No inspections yet</h3>
                <p className="text-sm text-slate-500 mt-1">Click New Draft to start an inspection.</p>
              </div>
            ) : (
              myInspections.map(insp => (
                <div key={insp.id} onClick={() => setLocation(`/engineer/inspection/${insp.id}`)} className="bg-white border border-slate-200 rounded-xl p-4 flex items-center justify-between gap-4 cursor-pointer hover:border-primary/40 hover:shadow-sm transition-all active:scale-[0.99]">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      {insp.status === "completed" ? (
                        <span className="shrink-0 flex items-center gap-1 px-2 py-0.5 rounded text-[10px] uppercase tracking-wider font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                          <Check className="w-3 h-3" /> Complete
                        </span>
                      ) : (
                        <span className="shrink-0 flex items-center gap-1 px-2 py-0.5 rounded text-[10px] uppercase tracking-wider font-bold bg-amber-50 text-amber-700 border border-amber-200">
                          <Edit2 className="w-3 h-3" /> Draft
                        </span>
                      )}
                      <span className="text-xs text-slate-400 shrink-0 font-medium">{new Date(insp.createdAt).toLocaleDateString()}</span>
                    </div>
                    <h3 className="font-bold text-slate-900 truncate">{insp.data.siteName || "Unnamed Site"}</h3>
                    <p className="text-sm text-slate-500 truncate">{insp.data.assetId || "No Asset ID"}</p>
                  </div>
                  <div className="shrink-0 w-8 h-8 rounded-full bg-slate-50 flex items-center justify-center border border-slate-100">
                    <ChevronRight className="w-4 h-4 text-slate-400" />
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </main>
    </div>
  );
}
